import {
  VariationAssessment,
  VariationAssessmentSummary,
  VariationLetterheadTemplate,
} from '../models'

const templates: VariationLetterheadTemplate[] = [
  {
    id: 'default',
    name: 'DCWC Victoria (Default)',
    companyName: 'Donald Cant Watts Corke (VIC) Pty Ltd',
    address: 'Level 5, 180 Flinders Street, Melbourne VIC 3000',
    phone: '+61 3 9690 2600',
    email: 'melbourne@dcwc.com.au',
    website: 'dcwc.com.au',
    abn: 'ABN 65 005 437 841',
  },
  {
    id: 'perth',
    name: 'DCWC Perth',
    companyName: 'Donald Cant Watts Corke (WA) Pty Ltd',
    address: 'Level 2, 30 The Esplanade, Perth WA 6000',
    phone: '+61 8 6109 1500',
    email: 'perth@dcwc.com.au',
    website: 'dcwc.com.au',
  },
]

const mockStatusAmounts = (overrides: Partial<Record<string, number>> = {}) => ({
  'rejected-withdrawn': 0,
  estimated: 0,
  submitted: 0,
  recommended: 0,
  endorsed: 0,
  ...overrides,
})

export const mockVariationAssessment: VariationAssessment = {
  meta: {
    id: 'VAR-2025-001',
    projectId: 'PROJ-001',
    projectName: 'Residential Development - Stage 2',
    mode: 'edit',
    revision: 0,
    status: 'estimated',
    statusLabel: 'Estimated',
    lastUpdated: '2025-11-18T10:00:00Z',
  },
  details: {
    variationNumber: 'VO-2025-473',
    contractorsVariationNumber: 'CR-2025-473',
    referenceSequenceNumber: '001',
    dcwcReference: 'DCWC-REF-2025-473',
    contractName: 'Residential Development - Stage 2',
    variationType: 'scopeChange',
    fundingSource: 'Owner contingency',
    reason: 'Extended asbestos removal scope',
    principal: 'Property Developers Ltd',
    contractor: 'Foundation Specialists',
    superintendent: 'Lisa Martinez',
    briefDescription: 'Prensa air monitoring, asbestos removal and soil disposal scope.',
    internalNotes: 'Internal-only notes for webcert users.',
    dateOfAssessment: '2025-11-12',
    submissionDate: '2025-11-13',
    responseDate: '2025-11-18',
    contractorsSubmissionAmount: 58000,
    documentStatus: 'estimated',
  },
  lineItems: [
    {
      id: 'li-1',
      itemNumber: 1,
      description: 'Prensa Air monitoring',
      parentId: null,
      children: [
        {
          id: 'li-1-1',
          itemNumber: 1,
          description: 'Air monitoring – shift 1',
          parentId: 'li-1',
          contractorSubmission: { quantity: 5, unit: 'days', rate: 1200, cost: 6000 },
          dcwcAssessment: { quantity: 4, unit: 'days', rate: 1180, cost: 4720 },
          comments: '',
          children: [],
        },
        {
          id: 'li-1-2',
          itemNumber: 2,
          description: 'Air monitoring – shift 2',
          parentId: 'li-1',
          contractorSubmission: { quantity: 5, unit: 'days', rate: 1280, cost: 6400 },
          dcwcAssessment: { quantity: 4, unit: 'days', rate: 1180, cost: 4720 },
          comments: 'Reduced to 4 shifts.',
          children: [],
        },
      ],
      contractorSubmission: { quantity: 10, unit: 'days', rate: 1280, cost: 128000 },
      dcwcAssessment: { quantity: 8, unit: 'days', rate: 1180, cost: 94400 },
      comments: 'Contractor assumed overtime rates.',
    },
    {
      id: 'li-2',
      itemNumber: 2,
      description: 'Prensa PM fee',
      parentId: null,
      children: [],
      contractorSubmission: { quantity: 1, unit: 'lot', rate: 18200, cost: 18200 },
      dcwcAssessment: { quantity: 1, unit: 'lot', rate: 14000, cost: 14000 },
      comments: '',
    },
    {
      id: 'li-3',
      itemNumber: 3,
      description: 'Asbestos removal contractor',
      parentId: null,
      children: [],
      contractorSubmission: { quantity: 1, unit: 'lot', rate: 17250, cost: 17250 },
      dcwcAssessment: { quantity: 1, unit: 'lot', rate: 17000, cost: 17000 },
      comments: 'Pending invoice review.',
    },
    {
      id: 'li-4',
      itemNumber: 4,
      description: 'Soil disposal',
      parentId: null,
      children: [],
      contractorSubmission: { quantity: 1, unit: 'lot', rate: 24000, cost: 24000 },
      dcwcAssessment: { quantity: 1, unit: 'lot', rate: 23000, cost: 23000 },
      comments: 'Awaiting weighbridge dockets.',
    },
  ],
  assessment: {
    tradeComponentAssessedAmount: 55102.22,
    profitAndOverheadsPercent: 10,
    totalRecommendedAdjustment: 58612.44,
    tradeComponentAssessedByDcwc: 55102.22,
    contractorAgreesToAssessment: true,
    contractorAmountReasonable: true,
    assessmentAlignedWithContractor: false,
    revisedAssessmentAgreedWithContractor: false,
    recommendedByQuantitySurveyor: true,
    tradeComponentComments: 'Contractor provided backup for quantities.',
    variationMarginComments: 'Using standard 10% P&O per contract.',
    assessmentComments: 'Recommendation subject to superintendent review.',
  },
  approval: {
    preparedBy: {
      name: 'Kelly Tran',
      title: 'Senior Quantity Surveyor',
      date: '2025-11-15',
    },
    approvedBy: {
      name: 'Leo Whitman',
      title: 'Project Manager',
      date: '2025-11-16',
    },
  },
  letterhead: {
    selectedTemplateId: 'default',
    includeCoverSheet: true,
    notes: 'Use revision zero letterhead for initial issue.',
    templates,
  },
}

export const mockVariationSummaries: VariationAssessmentSummary[] = [
  {
    id: 'VAR-2025-001',
    variationNumber: 'VO-2025-473',
    createdBy: 'Jane Doe',
    contractName: 'Building A Construction',
    description: 'Additional asbestos removal works',
    dateSubmitted: '2025-11-13',
    assessmentAmount: 40920,
    statusAmounts: mockStatusAmounts({ endorsed: 40920 }),
    statusLabel: 'PCG/Superintendent Endorsed',
    statusTone: 'neutral',
    workflowStatus: 'approved',
    updatedAt: '2025-11-18T10:00:00Z',
    variationType: 'Design Documentation',
    reason: 'Unforeseen Conditions',
    responseDate: '2025-11-20',
    fundingSource: 'Grants',
    variationAgeDays: 17,
    documentStatus: 'approved',
  },
  {
    id: 'VAR-2025-002',
    variationNumber: 'VO-2025-474',
    createdBy: 'Tom Wilson',
    contractName: 'Site Remediation Works',
    description: 'Extended air monitoring period',
    dateSubmitted: '2025-11-14',
    assessmentAmount: 12375,
    statusAmounts: mockStatusAmounts({ recommended: 12375 }),
    statusLabel: 'DCWC Recommended',
    statusTone: 'warning',
    workflowStatus: 'recommended',
    updatedAt: '2025-11-18T12:30:00Z',
    variationType: 'Client Change Request',
    reason: 'Weather Delay',
    responseDate: '2025-01-25',
    fundingSource: 'Equity Financing',
    variationAgeDays: 11,
    documentStatus: 'recommended',
  },
  {
    id: 'VAR-2025-003',
    variationNumber: 'VO-2025-475',
    createdBy: 'Emma Watson',
    contractName: 'Commercial Fit-out Project',
    description: 'Additional soil disposal requirements',
    dateSubmitted: '2025-11-16',
    assessmentAmount: 27720,
    statusAmounts: mockStatusAmounts({ submitted: 27720 }),
    statusLabel: 'Submitted',
    statusTone: 'info',
    workflowStatus: 'submitted',
    updatedAt: '2025-11-19T07:15:00Z',
    variationType: 'Authority Related',
    reason: 'Site Condition',
    responseDate: '2025-12-04',
    fundingSource: 'Business Credit Cards',
    variationAgeDays: 10,
    documentStatus: 'submitted',
  },
  {
    id: 'VAR-2025-004',
    variationNumber: 'VO-2025-476',
    createdBy: 'Tom Wilson',
    contractName: 'Infrastructure Upgrade Project',
    description: 'PM fees for project extension',
    dateSubmitted: '2025-11-17',
    assessmentAmount: 0,
    statusAmounts: mockStatusAmounts({ 'rejected-withdrawn': 0 }),
    statusLabel: 'Rejected/Withdrawn',
    statusTone: 'danger',
    workflowStatus: 'rejected',
    updatedAt: '2025-11-19T09:40:00Z',
    variationType: 'Extension of Time',
    reason: 'Contract Change',
    responseDate: '2025-02-16',
    fundingSource: 'Invoice Factoring',
    variationAgeDays: 2,
    documentStatus: 'rejected',
  },
  {
    id: 'VAR-2025-005',
    variationNumber: 'VO-2025-477',
    createdBy: 'James Chen',
    contractName: 'Residential Development - Stage 2',
    description: 'Preliminary cost estimate for foundation works',
    dateSubmitted: '2025-11-18',
    assessmentAmount: 35200,
    statusAmounts: mockStatusAmounts({ estimated: 35200 }),
    statusLabel: 'Estimated',
    statusTone: 'success',
    workflowStatus: 'estimated',
    updatedAt: '2025-11-19T10:15:00Z',
    variationType: 'Other',
    reason: 'Client Request',
    responseDate: '2025-09-14',
    fundingSource: 'Venture Capital',
    variationAgeDays: null,
    documentStatus: 'estimated',
  },
  {
    id: 'VAR-2025-006',
    variationNumber: 'VO-2025-478',
    createdBy: 'James Chen',
    contractName: 'Residential Development - Stage 2',
    description: 'Preliminary cost estimate for foundation works',
    dateSubmitted: '2025-11-18',
    assessmentAmount: 35200,
    statusAmounts: mockStatusAmounts(),
    statusLabel: 'Draft',
    statusTone: 'muted',
    workflowStatus: 'draft',
    updatedAt: '2025-11-19T10:16:00Z',
    variationType: 'Other',
    reason: 'Client Request',
    responseDate: '2025-09-14',
    fundingSource: 'Venture Capital',
    variationAgeDays: null,
    documentStatus: 'draft',
  },
]
